import {
  useStream as useLangChainStream,
  type UseStreamOptions,
} from "@langchain/react";
import { observer } from "mobx-react-lite";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useStore } from "../../stores";
import { type ChatMessage } from "./types";
import { useChatDeviceContext } from "./useChatDeviceContext";
import { getUserQueryPart } from "./utils";

export type StateType = { messages: ChatMessage[] };

function useStream(options: UseStreamOptions<StateType>) {
  return useLangChainStream<StateType>(options);
}

type NativeStreamContextType = ReturnType<typeof useStream>;
export type StreamContextType = Omit<NativeStreamContextType, "submit"> & {
  submit: NativeStreamContextType["submit"];
};
export type FormlyStreamSubmitOptions = NonNullable<
  Parameters<StreamContextType["submit"]>[1]
>;
export type FormlyStreamRespondOptions = NonNullable<
  Parameters<StreamContextType["respond"]>[1]
>;
const StreamContext = createContext<StreamContextType | undefined>(undefined);

const createUnavailableStreamContext = (
  client: NativeStreamContextType["client"]
): StreamContextType =>
  ({
    client,
    values: { messages: [] },
    messages: [],
    toolCalls: [],
    interrupts: [],
    error: null,
    isLoading: false,
    isThreadLoading: false,
    hydrationPromise: Promise.resolve(),
    threadId: null,
    subagents: new Map(),
    subgraphs: new Map(),
    subgraphsByNode: new Map(),
    interrupt: undefined,
    stop: async () => undefined,
    disconnect: async () => undefined,
    respond: async () => undefined,
    respondAll: async () => undefined,
    submit: async () => undefined,
  }) as unknown as StreamContextType;

const getSubmittedThreadName = (
  input: Parameters<StreamContextType["submit"]>[0]
) => {
  if (!input || typeof input !== "object" || !("messages" in input)) {
    return undefined;
  }

  const messages = input.messages;
  if (!Array.isArray(messages)) {
    return undefined;
  }

  const firstMessage = messages[0] as { content?: unknown } | undefined;
  const content = firstMessage?.content;
  const messageText =
    typeof content === "string"
      ? content
      : Array.isArray(content)
        ? content
            .filter(
              (part) => part?.type === "text" && typeof part.text === "string"
            )
            .map((part) => part.text)
            .join(" ")
        : undefined;

  return messageText ? getUserQueryPart(messageText) : undefined;
};

const truncateThreadName = (name: string | undefined) =>
  name && name.length > 300 ? name.substring(0, 300) + "..." : name;

const StreamSession = observer(
  ({ children, assistantId }: { children: ReactNode; assistantId: string }) => {
    const { agentStore } = useStore();
    const { orgId } = useChatDeviceContext();

    if (!orgId) {
      throw new Error("Organization ID is required");
    }

    // Use computed values from the store to minimize observable dependencies
    const currentThreadId = agentStore.currentThreadId;

    const handleThreadId = (id: string) => {
      agentStore.navigateToThread(id);
    };

    const streamValue = useStream({
      client: agentStore.client,
      assistantId,
      threadId: currentThreadId,
      messagesKey: "messages",
      onThreadId: handleThreadId,
    });
    const isThreadLoading =
      streamValue.isThreadLoading || streamValue.threadId !== currentThreadId;
    const messages = isThreadLoading ? [] : streamValue.messages;

    const submit: NativeStreamContextType["submit"] = useCallback(
      async (input, options) => {
        if (isThreadLoading || agentStore.currentThreadId !== currentThreadId) {
          throw new Error("Thread is still loading");
        }

        let nextOptions = options;
        if (!currentThreadId) {
          const threadName =
            truncateThreadName(getSubmittedThreadName(input)) || "Untitled";
          nextOptions = {
            ...options,
            metadata: {
              ...(options?.metadata ?? {}),
              name: threadName,
            },
          };
        }

        await streamValue.submit(input, nextOptions);
      },
      [agentStore, currentThreadId, streamValue, isThreadLoading]
    );

    const contextValue = useMemo<StreamContextType>(
      () =>
        Object.defineProperties(
          {},
          {
            ...Object.getOwnPropertyDescriptors(streamValue),
            isThreadLoading: {
              configurable: true,
              enumerable: true,
              value: isThreadLoading,
              writable: false,
            },
            messages: {
              configurable: true,
              enumerable: true,
              value: messages,
              writable: false,
            },
            submit: {
              configurable: true,
              enumerable: true,
              value: submit,
              writable: false,
            },
          }
        ) as StreamContextType,
      [streamValue, messages, submit, isThreadLoading]
    );

    useEffect(() => {
      if (streamValue.error) {
        agentStore.setError(streamValue.error);
      }
    }, [agentStore, streamValue.error]);

    return (
      <StreamContext.Provider value={contextValue}>
        {children}
      </StreamContext.Provider>
    );
  }
);

export const StreamProvider: React.FC<{ children: ReactNode }> = observer(
  ({ children }) => {
    const { agentStore } = useStore();

    // Get environment variables
    const apiUrl: string = window._env_.ASSISTANT_API_URL;
    // Use selected assistant ID from store
    const assistantId: string | null = agentStore.selectedAssistantId;

    if (!apiUrl) {
      throw new Error("Assistant API URL is required");
    }

    if (!assistantId) {
      const contextValue = createUnavailableStreamContext(agentStore.client);
      return (
        <StreamContext.Provider value={contextValue}>
          {children}
        </StreamContext.Provider>
      );
    }

    return <StreamSession assistantId={assistantId}>{children}</StreamSession>;
  }
);

// Create a custom hook to use the context
export const useStreamContext = (): StreamContextType => {
  const context = useContext(StreamContext);
  if (context === undefined) {
    throw new Error("useStreamContext must be used within a StreamProvider");
  }
  return context;
};

export default StreamContext;
